// Numeric geometry only: never retain image data, Blob URLs, or DOM nodes.
// Complete drawing code/URL keys invalidate geometry when the source changes.
const MAX_ENTRIES = 256;
const MAX_KEY_BYTES = 512 * 1024;
const dimensions = new Map();
const listeners = new Set();
let keyBytes = 0;
const bytes = (key) => key.length * 2;

export const previewImageDimensionKey = (asset) => {
  const code = String(asset?.source_code || "").trim();
  return String(asset?.source_language || "").toLowerCase() === "python" && code
    ? `python:${code}:${JSON.stringify(asset?.render_config || null)}`
    : `image:${asset?._preview_url || asset?._previewUrl || asset?.url || ""}`;
};

export const getPreviewImageDimensions = (key) => dimensions.get(key);
export const subscribePreviewImageDimensions = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const rememberPreviewImageDimensions = (key, width, height) => {
  if (!key || !(width > 0 && height > 0) || !Number.isFinite(width + height) || bytes(key) > MAX_KEY_BYTES) return;
  const previous = dimensions.get(key);
  if (previous?.width === width && previous?.height === height) return;
  if (previous) { dimensions.delete(key); keyBytes -= bytes(key); }
  dimensions.set(key, { width, height }); keyBytes += bytes(key);
  while (dimensions.size > MAX_ENTRIES || keyBytes > MAX_KEY_BYTES) {
    const oldest = dimensions.keys().next().value;
    dimensions.delete(oldest); keyBytes -= bytes(oldest);
  }
  listeners.forEach((listener) => listener(key));
};
export const clearPreviewImageDimensions = () => { dimensions.clear(); keyBytes = 0; };

// Lazy DOM images in the reader's invisible measuring copy may never load.
// Decode a rendered image once so all page copies share its real geometry.
const measuring = new Map();
export const measurePreviewImageDimensions = (key, source) => {
  if (dimensions.has(key)) return Promise.resolve(dimensions.get(key));
  if (!key || !source || typeof Image === "undefined") return Promise.resolve(undefined);
  if (measuring.has(key)) return measuring.get(key);
  const pending = new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      rememberPreviewImageDimensions(key, image.naturalWidth, image.naturalHeight);
      image.onload = image.onerror = null;
      resolve(dimensions.get(key));
    };
    image.onerror = () => { image.onload = image.onerror = null; resolve(undefined); };
    image.src = source;
  });
  measuring.set(key, pending);
  pending.finally(() => measuring.delete(key));
  return pending;
};
